import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Stack,
  Text,
} from '@eluve/components';
import { passwordRequirements } from '@eluve/utils';

const submissionTypeSchema = z.union([
  z.literal('login'),
  z.literal('register'),
]);

const formSchema = z
  .object({
    email: z.string().email(),
    type: submissionTypeSchema,
    password: passwordRequirements,
    confirmPassword: z.string().optional(),
  })
  .refine(
    (data) =>
      data.type === 'login' ? true : data.password === data.confirmPassword,
    {
      message: 'Passwords do not match',
      path: ['confirmPassword'],
    },
  );

type FormData = z.infer<typeof formSchema>;

export type EmailPasswordAuthFormData = Omit<FormData, 'confirmPassword'>;

export type AuthFormMode = 'login' | 'register';

export interface EmailPasswordAuthFormProps {
  initialEmail?: string;
  initialMode?: AuthFormMode;
  onSubmit: (
    data: EmailPasswordAuthFormData,
  ) => Promise<AuthFormMode | undefined>;
  onSendEmail?: (email: string) => Promise<void>;
  showMagicLinkButton?: boolean;
  showBaaAgreement?: boolean;
  hasAgreedToBaa?: boolean;
  onBaaAgreementChange?: (agreed: boolean) => void;
}

export const EmailPasswordAuthForm: React.FC<EmailPasswordAuthFormProps> = ({
  initialEmail,
  initialMode = 'login',
  onSubmit,
  onSendEmail,
  showMagicLinkButton = false,
  showBaaAgreement = false,
  hasAgreedToBaa = false,
  onBaaAgreementChange,
}) => {
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: initialEmail ?? '',
      password: '',
      type: initialMode,
    },
  });

  const submissionType = form.watch('type');

  const handleSubmit = async (data: FormData) => {
    const result = await onSubmit(data);
    if (result) {
      form.setValue('type', result);
    }
  };

  const handleSendEmail = async () => {
    const email = form.getValues('email');
    if (!email) {
      form.setError('email', {
        message: 'Please enter your email address first',
      });
      return;
    }

    if (!onSendEmail) {
      form.setError('root', {
        message: 'Send email functionality not available',
      });
      return;
    }

    setIsSendingEmail(true);
    try {
      await onSendEmail(email);
    } catch {
      // Error handling is done in the onSendEmail callback
      form.setError('root', {
        message: 'Failed to send email. Please try again.',
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="w-full space-y-2"
      >
        {form.formState.errors.root && (
          <FormMessage className="mb-2">
            {form.formState.errors.root.message}
          </FormMessage>
        )}
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder="Enter your email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <Input placeholder="Password" {...field} type="password" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {submissionType === 'register' && (
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm Password</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Confirm Password"
                    {...field}
                    type="password"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* BAA Agreement checkbox - only show when registering */}
        {submissionType === 'register' && showBaaAgreement && (
          <Stack align="start" className="pt-4" gap={2} direction="horizontal">
            <Checkbox
              checked={hasAgreedToBaa}
              onCheckedChange={(checked) =>
                onBaaAgreementChange?.(Boolean(checked))
              }
            />
            <Stack align="start" gap={1} className="leading-none">
              <Text inline size="xs" weight="regular">
                By signing up, you agree to our{' '}
                <a
                  className="underline"
                  href="https://www.eluve.com/terms"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms of Service,
                </a>{' '}
                <a
                  className="underline"
                  href="https://www.eluve.com/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>{' '}
                and, if applicable, our{' '}
                <a
                  className="underline"
                  href="https://www.eluve.com/business-associate-agreement"
                  target="_blank"
                  rel="noreferrer"
                >
                  Business Associate Agreement
                </a>
              </Text>
            </Stack>
          </Stack>
        )}

        <Stack gap={2} direction="horizontal">
          <Button
            type="submit"
            className="mt-2 flex-1"
            disabled={
              submissionType === 'register' &&
              showBaaAgreement &&
              !hasAgreedToBaa
            }
          >
            {submissionType === 'register'
              ? 'Create Account'
              : 'Login with Password'}
          </Button>
          {submissionType === 'login' && onSendEmail && showMagicLinkButton && (
            <Button
              onClick={handleSendEmail}
              disabled={isSendingEmail}
              className="flex-1"
            >
              {isSendingEmail ? 'Sending...' : 'Email Login Link'}
            </Button>
          )}
        </Stack>
      </form>
    </Form>
  );
};
